import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { useUserSettings } from "@/contexts"
import type { SelectableLanguageCode } from "@/models/i18n"
import type { UnitName } from "@/models/unit"
import { defaultDecimalFormatOptions, translateDecimal } from "@/utils/decimal"
import type { DecimalFormatOptions, DecimalOptions } from "@/utils/decimal"
import { resolveUnitName } from "@/utils/unit"

const PRINTED_DECIMAL_PATTERN = /^-?\d+(?:\.\d+)?$/

/**
 * Groups a printed decimal for the active locale and keeps its fraction length.
 * Fraction digits are pinned so `Number()` rounding cannot add a float tail.
 * This does not add amounts. Use `sumDecimalStrings` for that.
 */
const formatPrintedDecimal = (
  value: string | number | null | undefined,
  localeCode: string
): string => {
  if (value == null) {
    return ""
  }

  const trimmed = String(value).trim()
  if (!PRINTED_DECIMAL_PATTERN.test(trimmed)) {
    return trimmed
  }

  const fractionDigits = trimmed.split(".")[1]?.length ?? 0

  return new Intl.NumberFormat(localeCode, {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
    useGrouping: true,
  }).format(Number(trimmed))
}

export interface FormatDecimalCallOptions {
  /**
   * Format a value the API describes as printed. Keeps the printed fraction
   * length, does not convert units, and returns a non-decimal string unchanged.
   */
  printed?: boolean
  unit?: UnitName | string
}

export type FormatDecimal = (
  value: string | number | null | undefined,
  callOptions?: FormatDecimalCallOptions
) => string

interface UseFormatDecimalValue {
  formatDecimal: FormatDecimal
}

export const useFormatDecimal = (
  formatOptions: DecimalFormatOptions = {},
  options: DecimalOptions = {}
): UseFormatDecimalValue => {
  const { i18n } = useTranslation()
  const { unitsSystem, currency } = useUserSettings()

  const formatDecimal = useCallback(
    (
      value: string | number | null | undefined,
      callOptions?: FormatDecimalCallOptions
    ): string => {
      if (callOptions?.printed === true) {
        return formatPrintedDecimal(value, i18n.language)
      }

      const callUnit = callOptions?.unit
      const hasCallUnit = callUnit != null && callUnit.trim() !== ""
      const unit = hasCallUnit ? resolveUnitName(callUnit) : options.unit
      const formatted = translateDecimal({
        format: {
          ...defaultDecimalFormatOptions,
          ...formatOptions,
        },
        localeCode: i18n.language as SelectableLanguageCode,
        options: {
          ...options,
          currencyCode: currency,
          unit,
          unitsSystem,
        },
        value,
      })

      if (!hasCallUnit || unit != null) {
        return formatted
      }

      return `${formatted} ${callUnit.trim()}`
    },
    [currency, formatOptions, i18n.language, options, unitsSystem]
  )

  return useMemo(() => ({ formatDecimal }), [formatDecimal])
}
